package com.siriusxm.mce.auth.browser

import com.siriusxm.cmc.utils.IdToken
import com.siriusxm.pia.TokenAccess
import com.soywiz.krypto.SHA256
import com.soywiz.krypto.SecureRandom
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.forms.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.dom.get
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.random.Random
import kotlin.time.Clock
import kotlin.time.Instant

private val tokenParser = Json {
    ignoreUnknownKeys = true
}

/** Query parameters that appear on an OAuth callback and must not leak into a saved destination. */
private val oauthCallbackParams = setOf("code", "state", "error", "error_description")

/**
 * Base64url codec for the OAuth `state` payload. Padding is optional on decode so the value
 * survives any intermediary that strips `=`; we emit it unpadded.
 */
@OptIn(ExperimentalEncodingApi::class)
private val stateBase64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.PRESENT_OPTIONAL)

/**
 * The payload carried in the OAuth `state` parameter across the Cognito round-trip.
 * @property n a random nonce, verified against localStorage on return (CSRF/replay guard).
 * @property r the origin-relative destination to restore after login, if any.
 */
@Serializable
private data class OAuthState(val n: String, val r: String? = null)


/**
 * Manages OAuth token state in the browser.
 */
public class OAuthController(
    private val authUrl: String,
    private val clientId: String,
    private val redirectUrl: String,
    private val cognitoPrefix: String = "sxm",
    private val scopes: String = "email openid phone",
    private val pkceEnabled: Boolean
) : TokenAccess {
    override var accessToken: String? = null
    public var refreshToken: String? = null
    private var expiration: Instant = Clock.System.now()

    private val provider = CodeChallengeProvider(cognitoPrefix, CoroutineScope(Dispatchers.Default))

    private val jsonClient = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
            })
        }
    }

    /**
     * Install an auth provider on a http client configuration.
     */
    override fun installAuth(config: HttpClientConfig<*>) {
        config.apply {
            install(Auth) {
                bearer {
                    loadTokens {
                        BearerTokens(accessToken!!, refreshToken!!)
                    }
                    refreshTokens {
                        if (refreshToken != null) {
                            refresh(refreshToken!!)
                        }
                        BearerTokens(
                            accessToken ?: throw IllegalStateException("No access token"),
                            this@OAuthController.refreshToken ?: throw IllegalStateException("No refresh token")
                        )
                    }
                }
            }
        }
    }

    public val idToken: IdToken? by lazy {
        localStorage["${cognitoPrefix}_id_token"]?.split(".")?.get(1)?.let {
            try {
                tokenParser.decodeFromString(IdToken.serializer(), window.atob(it))
            } catch (_: Throwable) {
                null
            }
        }
    }

    public fun isLoggedIn(): Boolean {
        return accessToken != null && expiration > Clock.System.now()
    }

    /**
     * Initialize the controller.
     * @param requiredLogin if true, and the token is not active, the login flow will process, which
     * means a redirect to the login page.
     */
    public suspend fun init(requiredLogin: Boolean = true) {
        val currentUrl = Url(window.location.href)
        val code = currentUrl.parameters["code"]
        val error = currentUrl.parameters["error"]

        if (error != null) {
            // Cognito returned an error on the callback (e.g. the user denied consent). Clear the
            // pending nonce, strip the OAuth params from the URL, and RETURN so we don't fall
            // through to the re-login tail — re-firing login() here would loop the user straight
            // back to the (failed) authorize request.
            localStorage.removeItem("${cognitoPrefix}_oauth_state_nonce")
            restorePostLoginLocation(null)
            console.error("OAuth error: $error ${currentUrl.parameters["error_description"].orEmpty()}")
            return
        } else if (code != null) {
            val expectedNonce = localStorage["${cognitoPrefix}_oauth_state_nonce"]
            localStorage.removeItem("${cognitoPrefix}_oauth_state_nonce")
            val exchanged = try {
                exchangeCodeToTokens(code)
                true
            } catch (e: Throwable) {
                // Exchange failed (e.g. an already-consumed code on a reload). Clean the OAuth
                // params off the URL so a reload can't retry the dead code, and RETURN — don't
                // auto-retry, which could loop on a persistent failure. A manual reload now hits
                // a clean URL and starts a fresh login.
                restorePostLoginLocation(null)
                false
            }
            if (!exchanged) return

            // Resolve where to land: the saved deep-link destination (only if the returned
            // state's nonce matches what we stored, and the destination passes same-origin
            // validation) or, failing that, the cleaned current URL. Exactly one history write.
            val state = decodeState(currentUrl.parameters["state"])
            val nonceMatches = state != null && expectedNonce != null && state.n == expectedNonce
            val destination = if (nonceMatches) sanitizeReturnDestination(state?.r) else null

            restorePostLoginLocation(destination)
        } else {
            accessToken = localStorage["${cognitoPrefix}_access_token"]
            refreshToken = localStorage["${cognitoPrefix}_refresh_token"]
            expiration = localStorage["${cognitoPrefix}_expiration"]?.let {
                Instant.parse(it)
            } ?: Clock.System.now()

            if (accessToken != null && refreshToken != null && expiration < Clock.System.now()) {
                refresh(refreshToken!!)
            }
        }

        if (requiredLogin && !isLoggedIn()) {
            withContext(Dispatchers.Main) {
                login()
            }
        }
    }

    /**
     * Redirects to the hosted login page.
     *
     * Cognito only accepts the registered callback ([redirectUrl], the bare origin) as
     * `redirect_uri`, so we always send that. To still return the user to wherever they
     * deep-linked (e.g. `/image-playground/...` or a `#`-route), the current relative
     * destination is encoded into the OAuth `state` parameter, which round-trips through
     * Cognito untouched. A random nonce is stored alongside it in localStorage and verified
     * on return ([init]) to guard against forged/replayed `state`.
     */
    public suspend fun login() {
        val destination = currentRelativeDestination().takeUnless { isTrivialDestination(it) }
        val nonce = generateNonce()
        localStorage.setItem("${cognitoPrefix}_oauth_state_nonce", nonce)

        val url = URLBuilder("$authUrl/oauth2/authorize").apply {
            this.parameters.apply {
                set("client_id", clientId)
                set("response_type", "code")
                set("scope", scopes)
                set("redirect_uri", redirectUrl)
                set("state", encodeState(nonce, destination))
                if (pkceEnabled) {
                    set("code_challenge", provider.getCodeChallenge())
                    set("code_challenge_method", provider.getCodeChallengeMethod())
                }
            }
        }.buildString()

        window.location.href = url
    }

    /**
     * The current location as an origin-relative destination (`pathname + search + hash`) with
     * any OAuth callback params stripped from the query, so re-logging-in from a half-finished
     * callback URL doesn't capture stale `code`/`state`/`error` values.
     */
    private fun currentRelativeDestination(): String {
        val loc = window.location
        val rawSearch = loc.search.removePrefix("?")
        val cleanedSearch = if (rawSearch.isBlank()) {
            ""
        } else {
            rawSearch.split("&")
                .filter { it.isNotBlank() && it.substringBefore("=") !in oauthCallbackParams }
                .joinToString("&")
                .let { if (it.isEmpty()) "" else "?$it" }
        }
        return loc.pathname + cleanedSearch + loc.hash
    }

    /** A destination not worth restoring to — the bare root. */
    private fun isTrivialDestination(dest: String): Boolean = dest.isBlank() || dest == "/"

    /**
     * Cryptographically-random, URL-safe nonce used to correlate an OAuth `state` with this
     * browser (CSRF/replay guard). Uses krypto's [SecureRandom] (backed by the Web Crypto
     * `crypto.getRandomValues` CSPRNG on JS) rather than `kotlin.random.Random`, which is not
     * cryptographically secure.
     */
    @OptIn(ExperimentalEncodingApi::class)
    private fun generateNonce(): String =
        stateBase64.encode(SecureRandom.nextBytes(24)).trimEnd('=')

    /** Base64url-encode the `{ nonce, destination? }` struct for the OAuth `state` parameter. */
    @OptIn(ExperimentalEncodingApi::class)
    private fun encodeState(nonce: String, destination: String?): String {
        val json = tokenParser.encodeToString(OAuthState.serializer(), OAuthState(nonce, destination))
        return stateBase64.encode(json.encodeToByteArray()).trimEnd('=')
    }

    /** Decode the OAuth `state` parameter; returns null on any malformed/oversized input. */
    @OptIn(ExperimentalEncodingApi::class)
    private fun decodeState(raw: String?): OAuthState? {
        if (raw.isNullOrBlank()) return null
        return try {
            val json = stateBase64.decode(raw).decodeToString()
            tokenParser.decodeFromString(OAuthState.serializer(), json)
        } catch (_: Throwable) {
            null
        }
    }

    /**
     * Validate a return destination decoded from `state`. Only same-origin, relative
     * destinations are accepted; anything that could be an open redirect returns null, in which
     * case the caller falls back to the origin.
     */
    private fun sanitizeReturnDestination(r: String?): String? {
        if (r.isNullOrEmpty()) return null
        if (!(r.startsWith("/") || r.startsWith("#"))) return null
        if (r.startsWith("//") || r.startsWith("/\\")) return null
        if (r.contains("://") || r.contains("\\")) return null
        if (r.any { it.isWhitespace() || it.isISOControl() }) return null
        return try {
            // Belt-and-suspenders: resolve against the current origin and confirm the host is unchanged.
            if (Url("${window.location.origin}$r").host == Url(window.location.href).host) r else null
        } catch (_: Throwable) {
            null
        }
    }

    /**
     * After a successful code exchange, send the browser to the resolved [destination] (a
     * validated, origin-relative path) or, when null, just strip the OAuth params from the URL.
     * Performs exactly one history mutation.
     */
    private fun restorePostLoginLocation(destination: String?) {
        val origin = window.location.origin
        val currentPath = window.location.pathname
        val cleanedCurrent = "$origin$currentPath"

        if (destination == null) {
            window.history.replaceState(url = cleanedCurrent, data = null, title = "")
            return
        }

        // Path portion of the destination (before any query/hash). Destinations are built as
        // pathname + search + hash by currentRelativeDestination(), so a main-app hash route like
        // "/#aggregator/foo" has a path of "/" — the same document we're already on.
        val destinationPath = destination.substringBefore("#").substringBefore("?")
        val target = "$origin$destination"

        if (destinationPath == currentPath) {
            // Same document — only the search/hash differs (e.g. a hash route). Rewrite the URL in
            // place; no reload needed.
            //
            // IMPORTANT ordering dependency: replaceState does NOT fire a `hashchange` event, so
            // nothing reacts to this write on its own. It works only because the app reads
            // window.location.hash *once on startup*, AFTER awaiting OAuthController.init() in the
            // same coroutine (e.g. ServiceApplication.initCompose() awaits init() before
            // initNav()/HashNavigator reads the hash). If a caller ever runs init() without
            // awaiting it before nav startup, a hash deep-link would silently land at root. Keep
            // init() awaited ahead of navigation startup.
            window.history.replaceState(url = target, data = null, title = "")
        } else {
            // Different path → a separately-served bundle. Force an actual navigation.
            window.location.replace(target)
        }
    }

    /**
     * Clears all tokens.
     */
    public fun logout() {
        localStorage.removeItem("${cognitoPrefix}_access_token")
        localStorage.removeItem("${cognitoPrefix}_id_token")
        localStorage.removeItem("${cognitoPrefix}_refresh_token")

        accessToken = null
        refreshToken = null
        expiration = Clock.System.now()

        val url = URLBuilder("$authUrl/logout").apply {
            this.parameters.apply {
                set("client_id", clientId)
                set("response_type", "code")
                set("scope", scopes)
                set("redirect_uri", redirectUrl)
            }
        }.buildString()

        window.location.href = url
    }

    /**
     * Refresh using the current refresh token (if available)
     */
    public suspend fun refresh() {
        if (refreshToken != null) {
            refresh(refreshToken!!)
        }
    }

    private suspend fun refresh(refreshToken: String) {
        try {
            val token = jsonClient.submitForm(
                "$authUrl/oauth2/token",
                formParameters = Parameters.build {
                    append("grant_type", "refresh_token")
                    append("client_id", clientId)
                    append("redirect_uri", redirectUrl)
                    append("refresh_token", refreshToken)
                }) {
                expectSuccess = true
            }.body<Token>()

            processToken(token)
        } catch (e: ResponseException) {
            // ResponseException is the common supertype of ClientRequestException (4xx) and
            // ServerResponseException (5xx). With expectSuccess=true the token endpoint throws on
            // both, so catching the supertype ensures a flaky (5xx) endpoint still triggers the
            // clean-and-reload recovery instead of escaping as an unhandled rejection.
            // NB: never log the refresh-token value itself.
            println("Unable to refresh token (status=${e.response.status})")
            e.printStackTrace()
            // if refresh fails, clear everything
            accessToken = null
            this.refreshToken = null
            expiration = Clock.System.now()
            localStorage.removeItem("${cognitoPrefix}_access_token")
            localStorage.removeItem("${cognitoPrefix}_id_token")
            localStorage.removeItem("${cognitoPrefix}_refresh_token")
            localStorage.removeItem("${cognitoPrefix}_expiration")

            // reload the page, as we likely need to re-authorize.
            window.location.reload()
        }
    }

    private fun processToken(token: Token) {
        token.access_token.let { localStorage.setItem("${cognitoPrefix}_access_token", token.access_token) }
        token.id_token.let { localStorage.setItem("${cognitoPrefix}_id_token", token.id_token) }
        accessToken = token.access_token

        token.refresh_token?.let {
            localStorage.setItem("${cognitoPrefix}_refresh_token", token.refresh_token)
            refreshToken = it
        }
        expiration = Clock.System.now().plus(token.expires_in, DateTimeUnit.SECOND)
        localStorage.setItem("${cognitoPrefix}_expiration", expiration.toString())
    }

    private suspend fun exchangeCodeToTokens(code: String): String {
        // expectSuccess=true so a non-2xx token response throws (handled by init()'s try/catch)
        // rather than surfacing as a confusing deserialization error.
        val result = jsonClient.submitForm(
            "$authUrl/oauth2/token",
            formParameters = Parameters.build {
                append("grant_type", "authorization_code")
                append("client_id", clientId)
                append("redirect_uri", redirectUrl)
                append("code", code)
                if (pkceEnabled) {
                    append("code_verifier", provider.getCodeVerifier())
                }
            }) {
            expectSuccess = true
        }.body<Token>()

        processToken(result)

        // we are done with the verifier and can clear it
        provider.clearLocalStorage()
        return result.access_token
    }
}


@Suppress("PropertyName")
@Serializable
public data class Token(
    val access_token: String,
    val refresh_token: String? = null,
    val id_token: String,
    val token_type: String,
    val expires_in: Int
)

public class CodeChallengeProvider(
    private val cognitoPrefix: String,
    private val scope: CoroutineScope
) {
    private var codeChallengeDeferred: Deferred<Pair<String, String>>? = null

    private fun ensureInitialized() {
        if (codeChallengeDeferred == null) {
            codeChallengeDeferred = scope.async {
                generateCodeChallenge()
            }
        }
    }

    public suspend fun getCodeVerifier(): String {
        ensureInitialized()
        return codeChallengeDeferred!!.await().first
    }

    public suspend fun getCodeChallenge(): String {
        ensureInitialized()
        return codeChallengeDeferred!!.await().second
    }

    public fun getCodeChallengeMethod(): String = "S256"

    public fun clearLocalStorage() {
        localStorage.removeItem("${cognitoPrefix}_code_verifier")
    }

    @OptIn(ExperimentalEncodingApi::class)
    public fun generateCodeChallenge(): Pair<String, String> {
        val codeVerifier = localStorage["${cognitoPrefix}_code_verifier"] ?: buildString(128) {
            val chars = ('a'..'z') + ('A'..'Z') + ('0'..'9')
            repeat(128) {
                append(chars[Random.nextInt(chars.size)])
            }
        }.also {
            localStorage.setItem("${cognitoPrefix}_code_verifier", it)
        }

        // Hash the code verifier using SHA-256
        val hash = SHA256.digest(codeVerifier.encodeToByteArray())

        // Convert the hash to a URL-safe Base64-encoded string
        val codeChallenge = Base64.UrlSafe.encode(hash.bytes).trimEnd('=')

        isValid(codeVerifier)

        // Return the code verifier and code challenge
        return codeVerifier to codeChallenge
    }

    private fun isValid(codeVerifier: String): Boolean {
        val regex = Regex("^[A-Za-z0-9_-]{43,128}\$")
        val regexMatches = regex.matches(codeVerifier)
        return regexMatches
    }

}

/**
 * The dev-pool Cognito client for local console dev servers (`content-management-console-local`,
 * UCA-1270). Its callback and logout URLs are `http://localhost:8080` and one port per service:
 * management-console 8100, music 8110, sports 8120, epg 8130, catalog 8140, service-directory 8150,
 * mountain-services 8160, uca-discovery 8170.
 */
public const val LOCAL_DEV_CLIENT_ID: String = "78k8im8m85pbqn6d8fneurv74b"

/**
 * A utility for local development that is configured to connect to the dev instance and supports localhost.
 *
 * [clientId] defaults to [LOCAL_DEV_CLIENT_ID]. Tokens stored under [appPrefix] by a different client
 * fail to refresh, which clears them and sends the user to log in again.
 */
public suspend fun LocalDevController(
    scopes: List<String>,
    appPrefix: String,
    clientId: String = LOCAL_DEV_CLIENT_ID
): OAuthController {
    return OAuthController(
        "https://app-cnt-deliv-access-main-dev.auth.us-east-2.amazoncognito.com",
        clientId,
        window.location.origin,
        appPrefix,
        scopes.joinToString(" "),
        false
    ).also {
        it.init(true)
    }
}